import { Alert, Button, Flex, Input, Typography, notify } from 'front-commons/ds';
import { Typography as HypeTypography } from 'hype-ds';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SourcePosConflicts from 'pages/pos/Distributors/dialogs/SourcePosConflicts';
import DistributorsDragAndDrop from 'containers/DragAndDrop/DistributorsDragAndDrop';
import { ColumnsProps, DistributorsCallToActionProps } from 'containers/DragAndDrop/DistributorsDragAndDrop/interfaces';
import { BusinessUnitResponse, DistributorsSourceParams_DEPRECATED } from 'pages/pos/Distributors/interfaces';
import { deleteDistributors, orderOrAddDistributors } from 'services/pos';
import useDialog from 'stores/dialog';
import usePos from 'stores/pos';
import {
	handleAddDistributor,
	handleRemoveDistributor,
	handleReorderDistributor,
	handleRePositionDistributor,
} from './helpers';
import { CardsRenderProps } from './interfaces';
import useCustomer from 'stores/customer';
import { useMediaQuery } from 'front-commons/hooks';
import { theme } from 'front-commons/ds/core/tokens';
import useBasket from 'stores/basket';

export default function DistributorsTabs({
	type = 'SIMPLE',
	posList,
	sourceId,
	selected,
	isDrawer,
	available,
	CustomCTA,
	conflicts,
	sourceType,
	persistCTA,
	hideSearch = false,
	pharmacyId,
	alertMessage,
	simplePosCNPJ,
	forceShowColumns,
	customDescription,
	originalVersionDate,
	lastDistributorConfigSaved,
	emptyAvailableDistributorsMessage,
	onAdd,
	onRemove,
	onReorder,
	setSelected,
	handleLoading,
	onAfterSimpleAdd,
	onMirrorPOSConfig,
	onAfterSimpleRemove,
	validateDistributors,
	setLastDistributorConfigSaved,
	handleRefetchSelectedDistributors,
}: CardsRenderProps) {
	const navigate = useNavigate();
	const isTablet = useMediaQuery(`(min-width: ${theme.breakpoints.medium})`);

	const { handleOpenDialog } = useDialog();
	const {
		handleHasPermission,
		customerStore: {
			data: { hyperaRole },
			provider,
		},
	} = useCustomer();
	const { handleSelectPos, posStore, handleClearSelectedPos, handleUpdateDistributor } = usePos();
	const {
		handleGetBasketData,
		basketStore: { basket: basketData },
	} = useBasket();

	const [filter, setFilter] = useState('');

	const isPED = !!available.pedEnabled;
	const canBuy = !!selected?.distributors?.length && !!available?.distributors?.length;

	const hasPermission = handleHasPermission('DISTRIBUTOR_EDIT') || provider !== 'PARCEIRO_HYPERA';

	const handleFilter = ({ currentTarget: { value } }: React.ChangeEvent<HTMLInputElement>) => {
		setFilter(value);
	};

	const transactionIds = {
		[`${sourceType || 'businessUnit'}Id`]: sourceId,
	} as Record<`${DistributorsSourceParams_DEPRECATED['type']}Id`, DistributorsSourceParams_DEPRECATED['id']>;

	const handleAdd = useCallback(
		(distributorId: string, indexPosition?: number) => {
			const temp: BusinessUnitResponse['distributors'] = [];

			onAdd?.(distributorId, indexPosition);

			setSelected?.((prevState) => {
				return handleAddDistributor(prevState, available, sourceId, distributorId, indexPosition, temp);
			});

			if (type === 'MASSIVE') return;

			setTimeout(async () => {
				try {
					await orderOrAddDistributors({
						id: pharmacyId || '',
						data: {
							...transactionIds,
							distributors: temp.map(({ order = 0, id: distId }) => ({ distributorId: distId, order })),
						},
					});

					onAfterSimpleAdd?.();
				} catch (error) {
					notify.negative({ description: (error as any).data.errorDescription });
					handleRefetchSelectedDistributors?.();
				}
			}, 400);
		},
		[type, sourceId],
	);

	const handleRemove = useCallback(
		(id: string) => {
			onRemove?.(id);

			setSelected?.((prevState) => handleRemoveDistributor(prevState, sourceId, id));

			if (type === 'MASSIVE') return;

			setTimeout(async () => {
				try {
					await deleteDistributors({
						id: pharmacyId || '',
						distributors: {
							...transactionIds,
							distributorId: id,
						},
					});

					onAfterSimpleRemove?.(selected);

					setSelected?.((prevState) => handleRePositionDistributor(prevState, sourceId));
				} catch (error) {
					notify.negative({ description: (error as any).data.errorDescription });
					handleRefetchSelectedDistributors?.();
				}
			}, 400);
		},
		[type, sourceId, selected],
	);

	const handleRedirectToCatalogWithOpenedSideCart = () => {
		const haveToChangePos = posStore.selectedPos.id !== pharmacyId;

		if (haveToChangePos) {
			handleSelectPos({ posId: pharmacyId || '', isDistributorsPage: true, openSideBasket: true, cnpj: simplePosCNPJ });
			return;
		}

		navigate('/', { state: { openSideBasket: true } });
	};

	const handleOpenRemoveModal = async (distributorId: string) => {
		if (type === 'MASSIVE') {
			handleRemove(distributorId);
			return;
		}

		try {
			await handleGetBasketData({ loading: 'refetch' });

			const hasBasketWithDistributor = basketData?.baskets?.some((basket) => basket.distributorId === distributorId);

			if (!hasBasketWithDistributor) {
				handleRemove(distributorId);
				return;
			}

			await handleOpenDialog({
				heading: {
					title: 'Deseja continuar?',
				},
				content: {
					description:
						'Atenção! Caso tenha algum pedido em seu carrinho do(s) distribuidor(es) alterado(s), o mesmo será excluído. Deseja continuar?',
				},
				footer: {
					primaryButton: {
						label: 'Ver carrinho',
						onClick: handleRedirectToCatalogWithOpenedSideCart,
					},
					secondaryButton: {
						label: 'Confirmar',
						onClick: () => {
							if (distributorId) handleRemove(distributorId);
						},
					},
				},
			});
		} catch {
			console.log('Erro ao abrir modal de remoção');
		}
	};

	const handleReorder = useCallback(
		async (from: number, to: number) => {
			let oldState: BusinessUnitResponse['distributors'] = [];
			const newState: BusinessUnitResponse['distributors'] = [];

			onReorder?.(from, to);

			setSelected?.((prevState) => {
				oldState = prevState.find((tab) => tab.id === sourceId)?.distributors || [];
				return handleReorderDistributor(prevState, sourceId, from, to, newState);
			});

			if (type === 'MASSIVE') return;

			setTimeout(async () => {
				try {
					await orderOrAddDistributors({
						id: pharmacyId || '',
						data: {
							...transactionIds,
							distributors: newState.map(({ order = 0, id: distributorId }) => ({ distributorId, order })),
						},
					});
				} catch (error) {
					notify.negative({ description: (error as any).data.errorDescription });
					handleRefetchSelectedDistributors?.();
				}
			}, 400);
		},
		[sourceId, handleRefetchSelectedDistributors, pharmacyId, setSelected, type],
	);

	const handleClickMakeOrder = async () => {
		if (!canBuy)
			return notify.negative({
				title: 'Sua lista de distribuidores está vazia',
				description: 'Por favor selecione no mínimo 1 distribuidor para visualizar os produtos na loja.',
			});

		const isValidDistributors = validateDistributors?.();

		if (isValidDistributors) {
			handleClearSelectedPos();
			handleSelectPos({ posId: pharmacyId || '', isDistributorsPage: true, cnpj: simplePosCNPJ });
			return;
		}

		handleOpenDialog({
			heading: {
				title: 'Você precisa escolher seus distribuidores',
				showCloseButton: true,
			},
			content: {
				description:
					'Para visualizar os produtos na loja e realizar compras, é necessário configurar ao menos um distribuidor para cada indústria em que você possui atendimento.',
			},
			footer: {
				primaryButton: {
					label: 'Salvar e sair da página',
					onClick: () => navigate('/minhas-farmacias'),
				},
			},
		});
	};

	const handleOpenConflicts = () => {
		handleOpenDialog({
			heading: {
				title: 'PDVs onde não será aplicada a configuração dos distribuidores',
				showCloseButton: true,
				titleProps: {
					variant: 'Paragraph/Semibold',
				},
			},
			content: {
				description: <SourcePosConflicts buName={available.name} conflicts={conflicts} />,
			},
			footer: {
				primaryButton: {
					label: 'Ok, fechar',
				},
			},
		});
	};

	const getBUHelperText = () => {
		if (customDescription) return customDescription;

		if (isPED) return 'Agora você pode comprar pelo Hympar aproveitando as ofertas do seu distribuidor exclusivo!';

		if (available.name.includes('Marcas'))
			return (
				<Typography>
					Caso algum produto esteja indisponível no distribuidor selecionado no momento da compra, o{' '}
					<b>enviaremos automaticamente para o distribuidor seguinte</b>, respeitando a ordem de prioridade aqui
					definida.
				</Typography>
			);

		return (
			<Typography>
				Para comprar produtos de {available.name},{' '}
				<b>os distribuidores selecionados servirão apenas de apoio na comparação de preços</b>. O pedido será finalizado
				no distribuidor de sua escolha no momento da compra.
			</Typography>
		);
	};

	const hasSelectedDistributorsChanged =
		JSON.stringify(lastDistributorConfigSaved?.[sourceId] || []) !== JSON.stringify(selected?.distributors) &&
		!!selected?.distributors;

	const callToActionProps: DistributorsCallToActionProps = {
		type,
		currentBUName: available.name,
		loadingPharmacy: posStore.loading,
		hasSelectedDistributorsChanged,
		handleClickMakeOrder,
		setLastDistributorConfigSaved,
	};

	const filterComponent = !isPED && type === 'SIMPLE' && !hideSearch && (
		<Flex margin={{ medium: '0 0 -8px' }}>
			<Input
				name="search"
				onChange={handleFilter}
				value={filter}
				type="search"
				placeholder="Buscar distribuidor"
				disabled={handleLoading?.().get(['DISTRIBUTORS'])}
			/>
		</Flex>
	);

	const columnsProps: ColumnsProps = {
		isDrawer,
		availableProps: {
			buId: sourceId,
			isPed: isPED,
			filter,
			posList,
			selected,
			isTablet,
			available,
			CustomCTA,
			pharmacyId: pharmacyId || '',
			hideMinValue: type === 'MASSIVE',
			hasPermission,
			filterComponent,
			callToActionProps,
			selectedDistributors: selected,
			handleAdd,
		},
		selectedProps: {
			buId: sourceId,
			isPed: isPED,
			posList,
			isDrawer,
			isTablet,
			CustomCTA,
			pharmacyId: pharmacyId || '',
			hideMinValue: type === 'MASSIVE',
			hasPermission,
			filterComponent,
			businessUnitName: available.name,
			callToActionProps,
			originalVersionDate,
			selectedDistributors: selected,
			onRemove: handleOpenRemoveModal,
			onReorder: handleReorder,
			handleLoading,
			onMirrorPOSConfig,
		},
		onAdd: handleAdd,
		onReorder: handleReorder,
		onRemove: isDrawer ? handleRemove : handleOpenRemoveModal,
	};

	const showColumns = !!available.distributors?.length || type === 'SIMPLE' || forceShowColumns;

	useEffect(() => {
		if (pharmacyId === posStore.selectedPos.id && selected)
			handleUpdateDistributor({ ...selected, type: sourceType || 'businessUnit' });
	}, [selected]);

	return (
		<Flex direction="column" gap={{ small: '24px', medium: '32px' }} padding={{ small: '0', medium: '0 8px' }}>
			<Flex direction="column" gap={{ small: '24px', medium: isDrawer ? '4px' : '32px' }}>
				<Flex direction="column" gap={{ small: '8px', medium: '16px' }}>
					{!customDescription && (
						<Flex alignSelf="start" as={Typography} variant="Headline/H2 Bold" id="buTitle">
							Configuração dos distribuidores de {available.name}
						</Flex>
					)}

					<Flex
						gap={{ small: '24px', medium: '16px' }}
						direction={{ small: 'column', medium: isDrawer ? 'column' : 'row' }}
					>
						<Typography>{getBUHelperText()}</Typography>

						{type === 'MASSIVE' && (
							<Flex>
								<Alert type="note">
									<HypeTypography.Paragraph.M>
										<HypeTypography.Label.M>Atenção:</HypeTypography.Label.M> Caso tenha algum pedido em seus carrinhos
										dos distribuidores alterados, o mesmo será excluído.{' '}
										{hyperaRole && (
											<HypeTypography.Label.M>
												Qualquer alteração feita aqui vale apenas para sua conta e não altera os distribuidores
												definidos pelo cliente.
											</HypeTypography.Label.M>
										)}
									</HypeTypography.Paragraph.M>
								</Alert>
							</Flex>
						)}
					</Flex>
				</Flex>

				{!!Object.keys(conflicts || {}).length && !!available.distributors!.length && (
					<Alert
						type="info"
						title="A configuração dos distribuidores não será aplicada para alguns PDVs da sua seleção."
					>
						<Flex direction={{ small: 'column', medium: 'row' }} alignItems="start" justifyContent="space-between">
							<Typography>Você pode conferir por que isso aconteceu.</Typography>

							<Flex
								margin={{ medium: '-22px 0 0' }}
								as={Button}
								width={{ small: '100%', medium: 'fit-content' }}
								variant="secondary"
								onClick={handleOpenConflicts}
							>
								Visualizar PDVs
							</Flex>
						</Flex>
					</Alert>
				)}

				{hasSelectedDistributorsChanged && type === 'MASSIVE' && !isDrawer && (
					<Alert type="warn">As alterações realizadas na sua lista de distribuidores ainda não foram salvas.</Alert>
				)}

				{originalVersionDate && hyperaRole && (
					<Alert
						type={originalVersionDate === 'EMPTY' ? 'warn' : 'info'}
						toastMode
						preserveMobileRadius
						containerProps={{ marginTop: isDrawer ? '24px' : '0' }}
					>
						<Flex gap="0" direction="column">
							<Typography>
								{originalVersionDate === 'EMPTY' ? (
									'Não foi possível realizar esta ação. Este PDV ainda não possui distribuidores cadastrados.'
								) : (
									<>
										Você está vendo uma cópia da seleção de distribuidores do PDV, criada em{' '}
										{new Date(originalVersionDate)
											.toLocaleDateString('pt-br', { hour: 'numeric', minute: 'numeric' })
											.replace(',', ' às')}
										.
									</>
								)}
							</Typography>
							<Typography variant="Paragraph/Semibold">
								{originalVersionDate === 'EMPTY'
									? 'Faça a seleção manual dos distribuidores abaixo.'
									: 'Qualquer alteração feita aqui vale apenas para sua conta e não altera os distribuidores definidos pelo cliente.'}
							</Typography>
						</Flex>
					</Alert>
				)}

				{alertMessage && <Alert type="warn">{alertMessage}</Alert>}

				{!forceShowColumns && !available.distributors?.length && type === 'MASSIVE' && (
					<Alert
						type="warn"
						title={
							!emptyAvailableDistributorsMessage
								? 'Não foram encontrados distribuidores que atendam ao filtro selecionado.'
								: undefined
						}
					>
						{emptyAvailableDistributorsMessage || 'Por favor revise a seleção de UF e Farmácias.'}
					</Alert>
				)}
			</Flex>
			{showColumns && <DistributorsDragAndDrop {...columnsProps} />}
			{!showColumns && persistCTA && CustomCTA}
		</Flex>
	);
}
